/**
 * Mock Data Service
 *
 * Centralized service for managing mock data scenarios.
 * Provides methods to:
 * - Find scenarios based on query keywords
 * - Load mock responses from JSON files
 * - Get export file paths
 */

import { MOCK_SCENARIOS, MOCK_DATA_ENABLED, MockScenario } from './config';
import { StreamingQueryResponse } from '../streamingApi';

export class MockDataService {
  /**
   * Find a mock scenario based on query text
   * @param query - The user's query text
   * @returns The matching scenario or null
   */
  static findScenario(query: string): MockScenario | null {
    if (!MOCK_DATA_ENABLED || !query) {
      return null;
    }

    const queryLower = query.toLowerCase().trim();

    return MOCK_SCENARIOS.find(scenario =>
      scenario.enabled &&
      scenario.keywords.some(keyword => queryLower.includes(keyword.toLowerCase()))
    ) || null;
  }

  /**
   * Load mock response data for a scenario
   * @param scenarioId - The scenario ID
   * @returns Promise resolving to the mock response data
   */
  static async loadResponse(scenarioId: string): Promise<StreamingQueryResponse | null> {
    try {
      const scenario = MOCK_SCENARIOS.find(s => s.id === scenarioId);
      if (!scenario) {
        console.warn(`Mock scenario not found: ${scenarioId}`);
        return null;
      }

      // Dynamically import the JSON file
      const response = await import(`./responses/${scenario.responseFile}`);
      return response.default as StreamingQueryResponse;
    } catch (error) {
      console.error(`Failed to load mock response for scenario: ${scenarioId}`, error);
      return null;
    }
  }

  /**
   * Get export file path for a scenario
   * @param scenarioId - The scenario ID
   * @param format - The export format (pdf or powerpoint)
   * @returns The file path or null
   */
  static getExportFile(scenarioId: string, format: 'pdf' | 'powerpoint'): string | null {
    const scenario = MOCK_SCENARIOS.find(s => s.id === scenarioId);
    if (!scenario) {
      return null;
    }

    return scenario.files[format] || null;
  }

  /**
   * Check if mock data system is enabled
   */
  static isEnabled(): boolean {
    return MOCK_DATA_ENABLED;
  }

  /**
   * Get all enabled scenarios
   */
  static getEnabledScenarios(): MockScenario[] {
    return MOCK_SCENARIOS.filter(s => s.enabled);
  }

  /**
   * Check if a query should use mock data
   * @param query - The user's query text
   * @returns True if mock data should be used
   */
  static shouldUseMockData(query: string): boolean {
    return this.findScenario(query) !== null;
  }
}

export default MockDataService;
